import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withKeys as _withKeys, renderList as _renderList, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "resident-report-drawer__title" }
const _hoisted_2 = { class: "resident-report-drawer__close-trigger" }
const _hoisted_3 = { class: "resident-report-drawer__section resident-report-drawer__section--common" }
const _hoisted_4 = { class: "resident-report-drawer__section-content" }
const _hoisted_5 = { class: "resident-report-drawer__section" }
const _hoisted_6 = { class: "resident-report-drawer__section-content" }
const _hoisted_7 = ["aria-label", "onBlur", "onKeydown"]
const _hoisted_8 = ["aria-label", "onBlur", "onKeydown"]
const _hoisted_9 = {
  key: 3,
  class: "resident-report-drawer__deviation"
}
const _hoisted_10 = { class: "resident-report-drawer__section" }
const _hoisted_11 = { class: "resident-report-drawer__details-grid" }
const _hoisted_12 = { class: "resident-report-drawer__section" }
const _hoisted_13 = { class: "resident-report-drawer__documents" }
const _hoisted_14 = { class: "resident-report-drawer__footer" }
const _hoisted_15 = { class: "resident-report-drawer__footer-actions" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = ["disabled"]
const _hoisted_18 = { class: "resident-report-drawer-confirm__title" }
const _hoisted_19 = { class: "resident-report-drawer-confirm__actions" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = ["disabled"]
const _hoisted_22 = { class: "resident-report-sign-drawer__close-trigger" }
const _hoisted_23 = { class: "resident-report-sign-drawer__summary" }
const _hoisted_24 = { class: "resident-report-sign-drawer__summary-item" }
const _hoisted_25 = { class: "resident-report-sign-drawer__summary-value" }
const _hoisted_26 = { class: "resident-report-sign-drawer__summary-item" }
const _hoisted_27 = { class: "resident-report-sign-drawer__summary-value" }
const _hoisted_28 = { class: "resident-report-sign-drawer__summary-item" }
const _hoisted_29 = { class: "resident-report-sign-drawer__summary-value" }
const _hoisted_30 = { class: "resident-report-sign-drawer__certificates" }
const _hoisted_31 = { class: "resident-report-sign-drawer__footer" }
const _hoisted_32 = ["disabled"]
const _hoisted_33 = {
  class: "resident-report-sign-success-modal__icon",
  "aria-hidden": "true"
}
const _hoisted_34 = ["src"]
const _hoisted_35 = { class: "resident-report-sign-success-modal__details" }
const _hoisted_36 = { class: "resident-report-sign-success-modal__details-row" }
const _hoisted_37 = { class: "resident-report-sign-success-modal__details-row" }
const _hoisted_38 = { class: "resident-report-sign-success-modal__details-row" }

import { computed, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import type { TableColumnsType } from 'ant-design-vue';
import FormFileUpload, { type FileUploadItem } from '@/js/components/form/form-file-upload.vue';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
  showStcWarningNotification,
} from '@/js/components/stc-notification';
import { getAssetsPath } from '@/js/utils/getAssetsDir';
import {
  fetchPdfAsBase64,
  deleteReport,
  saveReport,
  signReport,
} from '../resident-report/report-api';
import {
  getReportDetailActions,
  getStatusModifier,
} from '../resident-report-detail/map-report-detail';
import { navigateToReportList } from '../resident-report-detail/get-report-id-from-url';
import { CryptoProGateway } from '../sign-widget/cryptopro-gateway';
import type { CertInfo, SignProfile } from '../sign-widget/sign-types';
import type { ReportEditPayload } from './report-edit-payload';

const SIGN_PROFILE: SignProfile = 'CAdES-BES';
type DetailFieldName =
  | 'comment'
  | 'projectNames'
  | 'projectReadiness'
  | 'rids'
  | 'commercialization'
  | 'stateSupport';

type MetricItem = {
  key: string;
  label: string;
  plan: number | null;
  fact: number | null;
  precision: number;
};
type MetricValueKey = 'plan' | 'fact';
type ReportDrawerMode = 'create' | 'edit';
type ReportFormState = {
  companyName: string;
  reportYear: string;
  details: Record<DetailFieldName, string>;
  metrics: Array<Pick<MetricItem, 'plan' | 'fact'>>;
  documentsCount: number;
};
type SignedReportInfo = {
  signer: string;
  serialNumber: string;
  signedAt: string;
};
type PreparedSignPayload = {
  id: number;
  content: string;
};

const TEXTAREA_MAX_LENGTH = 512;
const MOCK_CERTIFICATES_QUERY_PARAM = 'mockReportCertificates';
const MOCK_CERTIFICATES_STORAGE_KEY = 'stc:mock-report-certificates';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props, { expose: __expose }) {

const SUCCESS_ICON = getAssetsPath('/assets/images/icons/success-status.svg');

const open = ref(false);
const mode = ref<ReportDrawerMode>('create');
const reportId = ref<number | null>(null);
const reportStatus = ref<string | null>(null);
const isCloseConfirmOpen = ref(false);
const isDeleteConfirmOpen = ref(false);
const isNoCertificatesModalOpen = ref(false);
const isCertificateDrawerOpen = ref(false);
const isSignSuccessModalOpen = ref(false);
const isCertificatesLoading = ref(false);
const isSigning = ref(false);
const isSubmitting = ref(false);
const hasSubmitError = ref(false);
const isDeleting = ref(false);
const documentsUploadRef = ref<InstanceType<typeof FormFileUpload> | null>(null);
const documentItems = ref<FileUploadItem[]>([]);
const initialFormState = ref<ReportFormState | null>(null);
const certificates = ref<CertInfo[]>([]);
const selectedCertificateThumbprint = ref<string | null>(null);
const preparedSignPayload = ref<PreparedSignPayload | null>(null);
const signCreatedAt = ref(formatShortDate(new Date()));
const cryptoProGateway = new CryptoProGateway();
const signedReportInfo = reactive<SignedReportInfo>({
  signer: '-',
  serialNumber: '-',
  signedAt: '-',
});

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 3 }, (_, index) => {
  const year = currentYear - 1 + index;
  return { label: `${year} г.`, value: String(year) };
});

const form = reactive<
  Record<DetailFieldName, string> & { companyName: string; reportYear: string }
>({
  companyName: '',
  reportYear: String(currentYear),
  comment: '',
  projectNames: '',
  projectReadiness: '',
  rids: '',
  commercialization: '',
  stateSupport: '',
});

const metrics = reactive<MetricItem[]>([
  { key: 'revenue', label: 'Выручка, руб', plan: null, fact: null, precision: 0 },
  {
    key: 'invest_infra',
    label: 'Инвестиции в инфраструктуру ИНТЦ МГУ, руб',
    plan: null,
    fact: null,
    precision: 0,
  },
  { key: 'invest_rnd', label: 'Инвестиции в НИОКР, руб', plan: null, fact: null, precision: 0 },
  { key: 'export_share', label: 'Доля экспорта, %', plan: null, fact: null, precision: 2 },
  {
    key: 'employees',
    label: 'Численность работников организации, чел.',
    plan: null,
    fact: null,
    precision: 0,
  },
  {
    key: 'rid',
    label: 'Кол-во результатов интеллектуальной деятельности, шт',
    plan: null,
    fact: null,
    precision: 0,
  },
]);

const metricColumns: TableColumnsType<MetricItem> = [
  { title: 'Показатель', key: 'label', dataIndex: 'label' },
  { title: 'План', key: 'plan', width: 140 },
  { title: 'Факт', key: 'fact', width: 140 },
  { title: 'Отклонение, %', key: 'deviation', width: 140 },
];

const certificateColumns: TableColumnsType<CertInfo> = [
  { title: '', key: 'select', width: 40, fixed: 'left' },
  { title: 'Название', key: 'name', width: 170 },
  { title: 'Владелец', key: 'owner', width: 145 },
  { title: 'Организация', key: 'organization', width: 230 },
  { title: 'Номер', key: 'serialNumber', width: 120 },
  { title: 'Срок действия', key: 'validTo', width: 120 },
];

const detailFields: Array<{ name: DetailFieldName; label: string; required: boolean }> = [
  { name: 'comment', label: 'Общий комментарий', required: false },
  { name: 'projectNames', label: 'Наименование проекта', required: true },
  {
    name: 'projectReadiness',
    label: 'Стадия готовности реализованного НТП (УГТ), по ГОСТ-Р 71726-2024',
    required: true,
  },
  { name: 'rids', label: 'Перечень РИД, возникших при реализации НТП', required: true },
  {
    name: 'commercialization',
    label: 'Сведения о вовлечении результатов НТД в гражданский оборот',
    required: true,
  },
  {
    name: 'stateSupport',
    label: 'Сведения об использовании государственных мер поддержки',
    required: true,
  },
];

const areRequiredFieldsFilled = computed(
  () =>
    form.companyName.trim().length > 0 &&
    form.reportYear.trim().length > 0 &&
    metrics.every((metric) => metric.plan !== null && metric.fact !== null) &&
    detailFields
      .filter((field) => field.required)
      .every((field) => form[field.name].trim().length > 0),
);

const isActionDisabled = computed(
  () => isSubmitting.value || hasSubmitError.value || !areRequiredFieldsFilled.value,
);
const drawerTitle = computed(() =>
  mode.value === 'edit' ? 'Редактирование отчёта' : 'Новый отчёт',
);
const canDeleteReport = computed(
  () =>
    mode.value === 'edit' &&
    reportId.value !== null &&
    getReportDetailActions(getStatusModifier(reportStatus.value)).canDelete,
);
const signReportName = computed(() =>
  form.companyName.trim() ? `Отчет_${form.companyName.trim()}_${form.reportYear}` : 'Отчет',
);
const closeConfirmTitle = computed(() =>
  mode.value === 'edit' ? 'Отменить редактирование отчета?' : 'Отменить создание отчета?',
);
const hasUnsavedChanges = computed(() => {
  const initialState = initialFormState.value;
  if (!initialState) return false;

  return !isSameReportState(captureReportState(), initialState);
});

function getDefaultCompanyName(): string {
  const sidebarName = document.querySelector<HTMLElement>('[data-resident-name]');
  const sidebarValue = sidebarName?.textContent?.trim();
  if (sidebarValue) {
    return sidebarValue;
  }

  const input = document.querySelector<HTMLInputElement>(
    '#personalDataForm input[name="company_name"]',
  );
  return input?.value?.trim() ?? '';
}

function normalizeReportYear(value: string): string {
  return value.replace(/\s*г\.?\s*$/i, '').trim();
}

function createExistingDocumentItem(
  document: NonNullable<ReportEditPayload['documents']>[number],
): FileUploadItem {
  const fileName = document.name.replace(/^Файл\.\s*/i, '').trim() || document.name;
  const fileSize = document.size ?? 0;
  const file = new File([new ArrayBuffer(fileSize)], fileName, {
    type: 'application/octet-stream',
  });

  return {
    id: `existing-${document.url}-${fileName}`,
    file,
    status: 'ready',
    progress: 100,
  };
}

function applyEditPayload(payload: ReportEditPayload): void {
  reportId.value = payload.id ?? null;
  reportStatus.value = payload.status ?? null;
  form.companyName = payload.companyName;
  form.reportYear = normalizeReportYear(payload.reportYear);
  form.comment = payload.comment ?? '';
  form.projectNames = payload.projectNames ?? '';
  form.projectReadiness = payload.projectReadiness ?? '';
  form.rids = payload.rids ?? '';
  form.commercialization = payload.commercialization ?? '';
  form.stateSupport = payload.stateSupport ?? '';

  if (payload.metrics?.length) {
    payload.metrics.forEach((sourceMetric, index) => {
      const metric =
        (sourceMetric.key ? metrics.find((item) => item.key === sourceMetric.key) : null) ??
        metrics[index];
      if (!metric) return;

      metric.plan = sourceMetric.plan;
      metric.fact = sourceMetric.fact;
    });
  }

  documentItems.value = (payload.documents ?? []).map(createExistingDocumentItem);
}

function showDrawer(nextMode: ReportDrawerMode = 'create', payload?: ReportEditPayload): void {
  if (nextMode === 'create') {
    reportId.value = null;
    reportStatus.value = null;
    restoreReportState(getEmptyReportState());
    const companyName = getDefaultCompanyName();
    if (companyName) {
      form.companyName = companyName;
    }
  } else if (payload) {
    applyEditPayload(payload);
  }

  mode.value = nextMode;
  isCloseConfirmOpen.value = false;
  isDeleteConfirmOpen.value = false;
  hasSubmitError.value = false;
  initialFormState.value = captureReportState();
  open.value = true;
}

function closeDrawer(options: { reset?: boolean } = {}): void {
  if (options.reset) {
    resetReportForm();
    reportId.value = null;
    reportStatus.value = null;
    hasSubmitError.value = false;
  }
  isCloseConfirmOpen.value = false;
  isDeleteConfirmOpen.value = false;
  open.value = false;
}

function openDeleteConfirm(): void {
  if (!reportId.value || !canDeleteReport.value) return;
  isDeleteConfirmOpen.value = true;
}

function showDeleteConfirm(id: number): void {
  reportId.value = id;
  isDeleteConfirmOpen.value = true;
}

function cancelDeleteConfirm(): void {
  isDeleteConfirmOpen.value = false;
}

async function confirmDeleteReport(): Promise<void> {
  if (!reportId.value || isDeleting.value) return;

  isDeleting.value = true;

  try {
    const successMessage = await deleteReport(reportId.value);
    showStcSuccessNotification(successMessage || 'Отчёт успешно удалён');
    window.dispatchEvent(new CustomEvent('residentReports:changed'));
    isDeleteConfirmOpen.value = false;

    if (open.value) {
      closeDrawer({ reset: true });
    }

    if (document.querySelector('.resident-report-detail')) {
      navigateToReportList();
    }
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Не удалось удалить отчёт');
  } finally {
    isDeleting.value = false;
  }
}

function requestCloseDrawer(): void {
  if (!hasUnsavedChanges.value) {
    closeDrawer();
    return;
  }

  isCloseConfirmOpen.value = true;
}

function keepDrawerOpen(): void {
  isCloseConfirmOpen.value = false;
}

function confirmCloseDrawer(): void {
  closeDrawer({ reset: true });
}

function resetReportForm(): void {
  restoreReportState(initialFormState.value ?? getEmptyReportState());
}

function getEmptyReportState(): ReportFormState {
  return {
    companyName: '',
    reportYear: String(currentYear),
    details: detailFields.reduce(
      (acc, field) => {
        acc[field.name] = '';
        return acc;
      },
      {} as Record<DetailFieldName, string>,
    ),
    metrics: metrics.map(() => ({ plan: null, fact: null })),
    documentsCount: 0,
  };
}

function captureReportState(): ReportFormState {
  return {
    companyName: form.companyName,
    reportYear: form.reportYear,
    details: detailFields.reduce(
      (acc, field) => {
        acc[field.name] = form[field.name];
        return acc;
      },
      {} as Record<DetailFieldName, string>,
    ),
    metrics: metrics.map((metric) => ({ plan: metric.plan, fact: metric.fact })),
    documentsCount: documentItems.value.length,
  };
}

function restoreReportState(state: ReportFormState): void {
  form.companyName = state.companyName;
  form.reportYear = state.reportYear;
  detailFields.forEach((field) => {
    form[field.name] = state.details[field.name];
  });
  metrics.forEach((metric, index) => {
    const initialMetric = state.metrics[index];
    metric.plan = initialMetric?.plan ?? null;
    metric.fact = initialMetric?.fact ?? null;
  });
  documentsUploadRef.value?.reset();
  documentItems.value = [];
}

function isSameReportState(currentState: ReportFormState, initialState: ReportFormState): boolean {
  if (
    currentState.companyName !== initialState.companyName ||
    currentState.reportYear !== initialState.reportYear ||
    currentState.documentsCount !== initialState.documentsCount
  ) {
    return false;
  }

  const detailsChanged = detailFields.some(
    (field) => currentState.details[field.name] !== initialState.details[field.name],
  );
  if (detailsChanged) return false;

  return currentState.metrics.every((metric, index) => {
    const initialMetric = initialState.metrics[index];
    return metric.plan === initialMetric?.plan && metric.fact === initialMetric?.fact;
  });
}

function formatDeviation(metric: MetricItem): string {
  const plan = Number(metric.plan ?? 0);
  const fact = Number(metric.fact ?? 0);

  if (!plan) return '0.00';

  return (((fact - plan) / plan) * 100).toFixed(2);
}

function parseMetricValue(value: string, precision: number): number | null {
  const normalizedValue = value.replace(/\s+/g, '').replace(',', '.');
  if (!normalizedValue) return null;

  const parsedValue = Number(normalizedValue);
  if (!Number.isFinite(parsedValue)) return null;

  const positiveValue = Math.max(parsedValue, 0);
  return Number(positiveValue.toFixed(precision));
}

function formatMetricValue(value: number | null, precision: number): string {
  if (value === null) return '';

  return precision > 0 ? value.toFixed(precision) : String(value);
}

function getEditableCell(event: Event): HTMLElement | null {
  return event.target instanceof HTMLElement ? event.target : null;
}

function syncMetricCellText(metric: MetricItem, key: MetricValueKey, event: Event): void {
  const cell = getEditableCell(event);
  if (!cell) return;

  metric[key] = parseMetricValue(cell.textContent ?? '', metric.precision);
  cell.textContent = formatMetricValue(metric[key], metric.precision);
}

function blurEditableCell(event: Event): void {
  getEditableCell(event)?.blur();
}

async function openSignCertificateDrawer(): Promise<void> {
  if (isSubmitting.value) return;

  if (!areRequiredFieldsFilled.value) {
    showStcWarningNotification('Заполните все обязательные поля');
    return;
  }

  isSubmitting.value = true;
  isCertificatesLoading.value = true;
  signCreatedAt.value = formatShortDate(new Date());
  preparedSignPayload.value = null;

  try {
    const saveResult = await saveReport(buildReportFormData(), reportId.value);
    const savedReportId = saveResult.data?.id ?? reportId.value;
    const pdfUrl = saveResult.data?.pdfUrl;

    if (!savedReportId || !pdfUrl) {
      throw new Error('Сервер не вернул данные для подписи');
    }

    reportId.value = savedReportId;
    preparedSignPayload.value = {
      id: savedReportId,
      content: await fetchPdfAsBase64(pdfUrl),
    };
  } catch (error) {
    hasSubmitError.value = true;
    showStcErrorNotification(
      error instanceof Error ? error.message : 'Не удалось подготовить отчёт к подписанию',
    );
    return;
  } finally {
    isSubmitting.value = false;
  }

  try {
    await cryptoProGateway.checkAvailability();
    const certs = getMockCertificatesForPreview() ?? (await cryptoProGateway.getCertificates());

    if (!certs.length) {
      openNoCertificatesModal();
      return;
    }

    certificates.value = certs;
    selectedCertificateThumbprint.value = certs[0]?.thumbprint ?? null;
    isCertificateDrawerOpen.value = true;
  } catch {
    openNoCertificatesModal();
  } finally {
    isCertificatesLoading.value = false;
  }
}

function openNoCertificatesModal(): void {
  certificates.value = [];
  selectedCertificateThumbprint.value = null;
  isNoCertificatesModalOpen.value = true;
}

function getMockCertificatesForPreview(): CertInfo[] | null {
  if (process.env.NODE_ENV !== 'development') return null;

  const params = new URLSearchParams(window.location.search);
  const isEnabledByQuery = params.has(MOCK_CERTIFICATES_QUERY_PARAM);
  const isEnabledByStorage = localStorage.getItem(MOCK_CERTIFICATES_STORAGE_KEY) === 'true';

  if (!isEnabledByQuery && !isEnabledByStorage) return null;

  return [
    {
      thumbprint: 'MOCK_CERT_001',
      subjectName: 'CN=Иванов И. П., O=ООО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2026-01-01T00:00:00.000Z',
      validTo: '2026-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_002',
      subjectName: 'CN=Петров И. П., O=ОАО «Компания»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '123.456.789.0',
      validFrom: '2026-01-01T00:00:00.000Z',
      validTo: '2026-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
  ];
}

function closeNoCertificatesModal(): void {
  isNoCertificatesModalOpen.value = false;
}

function closeCertificateDrawer(): void {
  isCertificateDrawerOpen.value = false;
  selectedCertificateThumbprint.value = null;
  preparedSignPayload.value = null;
}

function closeSignSuccessModal(): void {
  isSignSuccessModalOpen.value = false;
  window.dispatchEvent(new CustomEvent('residentReports:changed'));
}

function selectCertificate(thumbprint: string): void {
  selectedCertificateThumbprint.value = thumbprint;
}

function getCertificateOwner(cert: CertInfo): string {
  return extractDnValue(cert.subjectName, 'CN') || cert.subjectName;
}

function getCertificateOrganization(cert: CertInfo): string {
  return extractDnValue(cert.subjectName, 'O');
}

function isCertificateExpired(cert: CertInfo): boolean {
  const validToDate = new Date(cert.validTo);
  return Number.isNaN(validToDate.getTime()) || validToDate.getTime() < Date.now();
}

function formatCertificateDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || '-';

  return date.toLocaleDateString('ru-RU');
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString('ru-RU');
}

function formatSignedAt(date: Date): string {
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  return `${time} / ${formatShortDate(date)}`;
}

function extractDnValue(subjectName: string, key: string): string {
  const parts = subjectName.split(',');
  const found = parts.find((part) => part.trim().startsWith(`${key}=`));
  return found ? found.split('=').slice(1).join('=').trim() : '';
}

async function signSelectedCertificate(): Promise<void> {
  if (!selectedCertificateThumbprint.value || isSigning.value) return;

  const selectedCertificate = certificates.value.find(
    (cert) => cert.thumbprint === selectedCertificateThumbprint.value,
  );
  if (!selectedCertificate) {
    showStcWarningNotification('Выберите сертификат для подписания');
    return;
  }

  if (!preparedSignPayload.value) {
    showStcErrorNotification('Нет данных для подписи. Сначала сохраните отчёт.');
    return;
  }

  isSigning.value = true;

  try {
    await cryptoProGateway.checkAvailability();
    const signature = await cryptoProGateway.signDetached(
      preparedSignPayload.value.content,
      selectedCertificate.thumbprint,
      SIGN_PROFILE,
    );

    await signReport(preparedSignPayload.value.id, {
      content: preparedSignPayload.value.content,
      signature,
    });

    signedReportInfo.signer = getCertificateOwner(selectedCertificate);
    signedReportInfo.serialNumber = selectedCertificate.serialNumber || '-';
    signedReportInfo.signedAt = formatSignedAt(new Date());
    isCertificateDrawerOpen.value = false;
    closeDrawer({ reset: true });
    isSignSuccessModalOpen.value = true;
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Не удалось подписать отчёт');
  } finally {
    isSigning.value = false;
  }
}

function appendMetric(formData: FormData, metric: MetricItem): void {
  formData.append(`${metric.key}_plan`, metric.plan === null ? '' : String(metric.plan));
  formData.append(`${metric.key}_fact`, metric.fact === null ? '' : String(metric.fact));
  formData.append(`${metric.key}_dev`, formatDeviation(metric));
}

function buildReportFormData(): FormData {
  const body = new FormData();

  if (reportId.value !== null) {
    body.append('Id', String(reportId.value));
  }

  body.append('companyName', form.companyName);
  body.append('reportYear', form.reportYear);
  metrics.forEach((metric) => appendMetric(body, metric));
  detailFields.forEach((field) => body.append(field.name, form[field.name]));
  const readyFiles = documentsUploadRef.value?.getReadyFiles() ?? [];
  readyFiles.forEach((file) => body.append('reportAttachments[]', file));

  return body;
}

async function saveDraftReport(): Promise<void> {
  if (isSubmitting.value) return;

  if (!areRequiredFieldsFilled.value) {
    showStcWarningNotification('Заполните все обязательные поля');
    return;
  }

  isSubmitting.value = true;
  documentsUploadRef.value?.setSubmittingState(true);

  try {
    const result = await saveReport(buildReportFormData(), reportId.value);
    const savedReportId = result.data?.id ?? reportId.value;
    if (savedReportId) {
      reportId.value = savedReportId;
    }

    showStcSuccessNotification(
      mode.value === 'edit' ? 'Отчет успешно сохранен.' : 'Отчет успешно создан.',
    );
    documentsUploadRef.value?.reset();
    window.dispatchEvent(new CustomEvent('residentReports:changed'));
    closeDrawer({ reset: true });
  } catch {
    hasSubmitError.value = true;
    showStcErrorNotification(
      mode.value === 'edit' ? 'Не удалось сохранить отчет.' : 'Не удалось создать отчет.',
    );
    documentsUploadRef.value?.resetSubmittingState();
  } finally {
    isSubmitting.value = false;
  }
}

async function submitReport(): Promise<void> {
  if (isSubmitting.value) return;

  if (!areRequiredFieldsFilled.value) {
    showStcWarningNotification('Заполните все обязательные поля');
    return;
  }

  isSubmitting.value = true;
  documentsUploadRef.value?.setSubmittingState(true);

  try {
    const result = await saveReport(buildReportFormData(), reportId.value);
    const savedReportId = result.data?.id ?? reportId.value;
    if (savedReportId) {
      reportId.value = savedReportId;
    }

    showStcSuccessNotification(result.message || 'Отчёт успешно сохранён');
    documentsUploadRef.value?.reset();
    window.dispatchEvent(new CustomEvent('residentReports:changed'));
    closeDrawer({ reset: true });
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Не удалось сохранить отчёт');
    documentsUploadRef.value?.resetSubmittingState();
  } finally {
    isSubmitting.value = false;
  }
}

__expose({ open: showDrawer, requestDelete: showDeleteConfirm });

return (_ctx: any,_cache: any) => {
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_table = _resolveComponent("a-table")!
  const _component_a_textarea = _resolveComponent("a-textarea")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_drawer = _resolveComponent("a-drawer")!
  const _component_a_modal = _resolveComponent("a-modal")!
  const _component_a_radio = _resolveComponent("a-radio")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_a_drawer, {
      width: 1280,
      open: open.value,
      class: "resident-report-drawer",
      "body-style": { paddingBottom: '32px' },
      "footer-style": { padding: '20px 40px 40px 40px' },
      "header-style": { padding: '32px 40px' },
      closable: true,
      onClose: requestCloseDrawer
    }, {
      title: _withCtx(() => [
        _createElementVNode("span", _hoisted_1, _toDisplayString(drawerTitle.value), 1)
      ]),
      closeIcon: _withCtx(() => [
        _createElementVNode("span", _hoisted_2, [
          _createVNode(_unref(CloseOutlined))
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_14, [
          _cache[8] || (_cache[8] = _createElementVNode("div", { class: "resident-report-drawer__footer-start" }, null, -1)),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-secondary btn-medium",
              onClick: requestCloseDrawer
            }, " Отмена "),
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-secondary btn-medium",
              disabled: isActionDisabled.value,
              onClick: saveDraftReport
            }, _toDisplayString(isSubmitting.value ? 'Сохранение...' : 'Сохранить черновик'), 9, _hoisted_16),
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-primary btn-medium",
              disabled: isActionDisabled.value,
              onClick: openSignCertificateDrawer
            }, _toDisplayString(isSubmitting.value ? 'Проверка...' : 'Подписать и отправить'), 9, _hoisted_17)
          ])
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_a_form, {
          layout: "vertical",
          class: "resident-report-drawer__form"
        }, {
          default: _withCtx(() => [
            _createElementVNode("section", _hoisted_3, [
              _cache[3] || (_cache[3] = _createElementVNode("h3", { class: "resident-report-drawer__section-title" }, "1. Общее", -1)),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Наименование организации",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.companyName,
                      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.companyName) = $event)),
                      placeholder: "ООО «Компания_название»",
                      disabled: ""
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Отчётный период",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: form.reportYear,
                      "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.reportYear) = $event)),
                      options: _unref(yearOptions)
                    }, null, 8, ["value", "options"])
                  ]),
                  _: 1
                })
              ])
            ]),
            _createElementVNode("section", _hoisted_5, [
              _cache[4] || (_cache[4] = _createElementVNode("h3", { class: "resident-report-drawer__section-title" }, "2. Показатели*", -1)),
              _cache[5] || (_cache[5] = _createElementVNode("p", { class: "resident-report-drawer__section-note" }, "*За выбранный отчётный период.", -1)),
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_component_a_table, {
                  class: "resident-report-drawer__metrics-table",
                  columns: metricColumns,
                  "data-source": metrics,
                  pagination: false,
                  bordered: true,
                  "row-key": "key"
                }, {
                  bodyCell: _withCtx(({ column, record }) => [
                    (column.key === 'label')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(_toDisplayString(record.label), 1)
                        ], 64))
                      : (column.key === 'plan')
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 1,
                            class: "resident-report-drawer__metric-cell",
                            contenteditable: "true",
                            inputmode: "decimal",
                            role: "textbox",
                            "aria-label": `${record.label}: план`,
                            onBlur: ($event: any) => (syncMetricCellText(record, 'plan', $event)),
                            onKeydown: _withKeys(_withModifiers(blurEditableCell, ["prevent"]), ["enter"])
                          }, _toDisplayString(formatMetricValue(record.plan, record.precision)), 41, _hoisted_7))
                        : (column.key === 'fact')
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 2,
                              class: "resident-report-drawer__metric-cell",
                              contenteditable: "true",
                              inputmode: "decimal",
                              role: "textbox",
                              "aria-label": `${record.label}: факт`,
                              onBlur: ($event: any) => (syncMetricCellText(record, 'fact', $event)),
                              onKeydown: _withKeys(_withModifiers(blurEditableCell, ["prevent"]), ["enter"])
                            }, _toDisplayString(formatMetricValue(record.fact, record.precision)), 41, _hoisted_8))
                          : (column.key === 'deviation')
                            ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(formatDeviation(record)), 1))
                            : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["data-source"])
              ])
            ]),
            _createElementVNode("section", _hoisted_10, [
              _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "resident-report-drawer__section-title" }, "3. Детали", -1)),
              _createElementVNode("div", _hoisted_11, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(detailFields, (field) => {
                  return _createVNode(_component_a_form_item, {
                    key: field.name,
                    class: "stc-ant-field-skin resident-report-drawer__textarea-field",
                    label: field.label,
                    required: field.required
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_textarea, {
                        value: form[field.name],
                        "onUpdate:value": ($event: any) => ((form[field.name]) = $event),
                        maxlength: TEXTAREA_MAX_LENGTH,
                        placeholder: "Текст",
                        "auto-size": { minRows: 6, maxRows: 6 },
                        "show-count": ""
                      }, null, 8, ["value", "onUpdate:value"])
                    ]),
                    _: 2
                  }, 1032, ["label", "required"])
                }), 64))
              ])
            ]),
            _createElementVNode("section", _hoisted_12, [
              _cache[7] || (_cache[7] = _createElementVNode("h3", { class: "resident-report-drawer__section-title" }, "4. Документы", -1)),
              _createElementVNode("div", _hoisted_13, [
                _createVNode(FormFileUpload, {
                  ref_key: "documentsUploadRef",
                  ref: documentsUploadRef,
                  modelValue: documentItems.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((documentItems).value = $event))
                }, null, 8, ["modelValue"])
              ])
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(_component_a_modal, {
      open: isCloseConfirmOpen.value,
      class: "resident-report-drawer-confirm",
      footer: null,
      closable: false,
      "mask-closable": false,
      centered: "",
      width: "480px"
    }, {
      default: _withCtx(() => [
        _createElementVNode("h3", _hoisted_18, _toDisplayString(closeConfirmTitle.value), 1),
        _cache[9] || (_cache[9] = _createElementVNode("p", { class: "resident-report-drawer-confirm__description" }, [
          _createTextVNode(" Все загруженные файлы и внесенные изменения "),
          _createElementVNode("br"),
          _createTextVNode(" будут утеряны. Вы уверены? ")
        ], -1)),
        _cache[10] || (_cache[10] = _createElementVNode("div", {
          class: "resident-report-drawer-confirm__divider",
          "aria-hidden": "true"
        }, null, -1)),
        _createElementVNode("div", { class: "resident-report-drawer-confirm__actions" }, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-secondary btn-medium",
            onClick: keepDrawerOpen
          }, " Нет "),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-error btn-medium",
            onClick: confirmCloseDrawer
          }, " Да, отменить ")
        ])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(_component_a_modal, {
      open: isDeleteConfirmOpen.value,
      class: "resident-report-drawer-confirm",
      footer: null,
      closable: false,
      "mask-closable": false,
      centered: "",
      width: "480px"
    }, {
      default: _withCtx(() => [
        _cache[11] || (_cache[11] = _createElementVNode("h3", { class: "resident-report-drawer-confirm__title" }, "Удалить отчет?", -1)),
        _cache[12] || (_cache[12] = _createElementVNode("p", { class: "resident-report-drawer-confirm__description" }, [
          _createTextVNode(" Все загруженные файлы и внесенные параметры "),
          _createElementVNode("br"),
          _createTextVNode(" будут удалены. Вы уверены? ")
        ], -1)),
        _cache[13] || (_cache[13] = _createElementVNode("div", {
          class: "resident-report-drawer-confirm__divider",
          "aria-hidden": "true"
        }, null, -1)),
        _createElementVNode("div", _hoisted_19, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-secondary btn-medium",
            disabled: isDeleting.value,
            onClick: cancelDeleteConfirm
          }, " Нет ", 8, _hoisted_20),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-error btn-medium",
            disabled: isDeleting.value,
            onClick: confirmDeleteReport
          }, _toDisplayString(isDeleting.value ? 'Удаление...' : 'Да, удалить'), 9, _hoisted_21)
        ])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(_component_a_modal, {
      open: isNoCertificatesModalOpen.value,
      class: "resident-report-no-certs-modal",
      footer: null,
      closable: false,
      "mask-closable": false,
      centered: "",
      width: "480px"
    }, {
      default: _withCtx(() => [
        _cache[15] || (_cache[15] = _createElementVNode("h3", { class: "resident-report-no-certs-modal__title" }, "Нет сертификатов", -1)),
        _cache[16] || (_cache[16] = _createElementVNode("p", { class: "resident-report-no-certs-modal__description" }, " Убедитесь, что КриптоПро CSP и браузерный плагин установлены, а носитель ключа (токен/смарт-карта) подключён к компьютеру. ", -1)),
        _cache[17] || (_cache[17] = _createElementVNode("div", {
          class: "resident-report-no-certs-modal__divider",
          "aria-hidden": "true"
        }, null, -1)),
        _createElementVNode("div", { class: "resident-report-no-certs-modal__actions" }, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-soft",
            onClick: closeNoCertificatesModal
          }, "Закрыть"),
          _cache[14] || (_cache[14] = _createElementVNode("a", {
            class: "btn btn-primary",
            href: "https://docs.cryptopro.ru/cades/plugin",
            target: "_blank",
            rel: "noopener noreferrer"
          }, " Инструкция по настройке ", -1))
        ])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(_component_a_drawer, {
      width: 1040,
      open: isCertificateDrawerOpen.value,
      class: "resident-report-sign-drawer",
      closable: true,
      "body-style": { paddingBottom: '96px' },
      onClose: closeCertificateDrawer
    }, {
      title: _withCtx(() => [...(_cache[18] || (_cache[18] = [
        _createElementVNode("span", { class: "resident-report-sign-drawer__title" }, "Подписать отчет", -1)
      ]))]),
      closeIcon: _withCtx(() => [
        _createElementVNode("span", _hoisted_22, [
          _createVNode(_unref(CloseOutlined))
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_31, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-soft",
            onClick: closeCertificateDrawer
          }, "Отмена"),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-primary",
            disabled: !selectedCertificateThumbprint.value || isSigning.value,
            onClick: signSelectedCertificate
          }, _toDisplayString(isSigning.value ? 'Подписание...' : 'Подписать'), 9, _hoisted_32)
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_23, [
          _createElementVNode("div", _hoisted_24, [
            _cache[19] || (_cache[19] = _createElementVNode("span", { class: "resident-report-sign-drawer__summary-label" }, "Название отчета", -1)),
            _createElementVNode("span", _hoisted_25, _toDisplayString(signReportName.value), 1)
          ]),
          _createElementVNode("div", _hoisted_26, [
            _cache[20] || (_cache[20] = _createElementVNode("span", { class: "resident-report-sign-drawer__summary-label" }, "Отчетный период", -1)),
            _createElementVNode("span", _hoisted_27, _toDisplayString(form.reportYear) + " г.", 1)
          ]),
          _createElementVNode("div", _hoisted_28, [
            _cache[21] || (_cache[21] = _createElementVNode("span", { class: "resident-report-sign-drawer__summary-label" }, "Дата создания", -1)),
            _createElementVNode("span", _hoisted_29, _toDisplayString(signCreatedAt.value), 1)
          ])
        ]),
        _createElementVNode("section", _hoisted_30, [
          _cache[23] || (_cache[23] = _createElementVNode("h3", { class: "resident-report-sign-drawer__section-title" }, "Сертификаты", -1)),
          _cache[24] || (_cache[24] = _createElementVNode("p", { class: "resident-report-sign-drawer__section-description" }, " Выберите действующий сертификат для подписания. ", -1)),
          _createVNode(_component_a_table, {
            class: "resident-report-sign-drawer__certificates-table",
            columns: certificateColumns,
            "data-source": certificates.value,
            pagination: false,
            bordered: true,
            loading: isCertificatesLoading.value,
            scroll: { y: 500, x: 740 },
            "row-key": "thumbprint"
          }, {
            bodyCell: _withCtx(({ column, record }) => [
              (column.key === 'select')
                ? (_openBlock(), _createBlock(_component_a_radio, {
                    key: 0,
                    checked: selectedCertificateThumbprint.value === record.thumbprint,
                    onChange: ($event: any) => (selectCertificate(record.thumbprint))
                  }, null, 8, ["checked", "onChange"]))
                : (column.key === 'name')
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createTextVNode(_toDisplayString(getCertificateOwner(record)), 1)
                    ], 64))
                  : (column.key === 'owner')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                        _createTextVNode(_toDisplayString(getCertificateOwner(record)), 1)
                      ], 64))
                    : (column.key === 'organization')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                          _createTextVNode(_toDisplayString(getCertificateOrganization(record) || '-'), 1)
                        ], 64))
                      : (column.key === 'serialNumber')
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                            _createTextVNode(_toDisplayString(record.serialNumber), 1)
                          ], 64))
                        : (column.key === 'validTo')
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 5,
                              class: _normalizeClass(["resident-report-sign-drawer__cert-validity", { 'is-expired': isCertificateExpired(record) }])
                            }, [
                              _cache[22] || (_cache[22] = _createElementVNode("span", {
                                class: "resident-report-sign-drawer__cert-validity-dot",
                                "aria-hidden": "true"
                              }, null, -1)),
                              _createTextVNode(" " + _toDisplayString(formatCertificateDate(record.validTo)), 1)
                            ], 2))
                          : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["data-source", "loading"])
        ])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(_component_a_modal, {
      open: isSignSuccessModalOpen.value,
      class: "resident-report-sign-success-modal",
      footer: null,
      closable: false,
      "mask-closable": false,
      centered: "",
      width: "420px"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_33, [
          _createElementVNode("img", {
            src: _unref(SUCCESS_ICON),
            alt: ""
          }, null, 8, _hoisted_34)
        ]),
        _cache[28] || (_cache[28] = _createElementVNode("h3", { class: "resident-report-sign-success-modal__title" }, "Отчет подписан и отправлен", -1)),
        _createElementVNode("dl", _hoisted_35, [
          _createElementVNode("div", _hoisted_36, [
            _cache[25] || (_cache[25] = _createElementVNode("dt", null, "Подписант", -1)),
            _createElementVNode("dd", null, _toDisplayString(signedReportInfo.signer), 1)
          ]),
          _createElementVNode("div", _hoisted_37, [
            _cache[26] || (_cache[26] = _createElementVNode("dt", null, "Сертификат, №", -1)),
            _createElementVNode("dd", null, _toDisplayString(signedReportInfo.serialNumber), 1)
          ]),
          _createElementVNode("div", _hoisted_38, [
            _cache[27] || (_cache[27] = _createElementVNode("dt", null, "Дата подписания", -1)),
            _createElementVNode("dd", null, _toDisplayString(signedReportInfo.signedAt), 1)
          ])
        ]),
        _createElementVNode("div", { class: "continue-button-container" }, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-outline btn-medium",
            onClick: closeSignSuccessModal
          }, " Продолжить ")
        ])
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})